import React, { useCallback, useMemo, useState, useEffect } from 'react'
import { debounce } from 'utils/async'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import Input from 'core/elements/input/Input'

export interface MultiArraySearchFilterProps {
  data: Record<string, any[]> // Object containing multiple arrays
  onSearch: (filteredData: Record<string, any[]>) => void
  searchKeys: string[] // Keys to filter on
  placeholder?: string
}

const useStyles = makeStyles<Theme>((theme) => ({
  searchBar: {
    outline: 'none',
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: 4,
    margin: 0,
    '& > .inputFrame': {
      minWidth: 290,
      padding: '2px 8px 2px 8px',
      backgroundColor: theme.palette.common.white,
      color: 'inherit',
    },
    '& .label, & .input, & .icon': {
      backgroundColor: 'inherit',
      color: 'inherit',
    },
  },
}))

export default function MultiArraySearchFilter({
  data,
  onSearch,
  searchKeys,
  placeholder,
}: MultiArraySearchFilterProps) {
  const classes = useStyles()
  const [searchTerm, setSearchTerm] = useState<string>('')

  const debouncedSearch = useMemo(
    () =>
      debounce<[string], void>((term) => {
        if (!data || Object.values(data).some((v) => v?.length === 0)) return
        const filteredData = Object.fromEntries(
          Object.entries(data)?.map(([key, array]) => [
            key,
            term
              ? array?.filter((item) =>
                  searchKeys?.some((searchKey) =>
                    item?.[searchKey]?.toLowerCase().includes(term.toLowerCase()),
                  ),
                )
              : array,
          ]),
        )
        onSearch(filteredData)
      }, 300),
    [data, searchKeys, onSearch],
  )

  const handleOnChange = useCallback(
    ({ target: { value } }) => {
      setSearchTerm(value)
      debouncedSearch(value)
    },
    [debouncedSearch],
  )

  useEffect(() => {
    return () => {
      debouncedSearch.cancel()
    }
  }, [debouncedSearch])

  return (
    <Input
      compact
      icon="search"
      placeholder={placeholder}
      className={classes.searchBar}
      value={searchTerm}
      onChange={handleOnChange}
      type="search"
    />
  )
}
